/**
 * Where an SMS may go (plan-005 OP-11, design §18.10.3, DD5-13, contract IC-53; FR-SMS-8).
 *
 * **Mobile ranges are data**: {@link SMS_MOBILE_RANGE} is the closed list of countries WeTalk can send
 * an SMS to, each with the pattern its mobile numbers match. The configured country allow-list
 * (`packages/config`, OP-12) must be a subset of its keys. There is no phone-number library in the
 * runtime image for one country.
 *
 * NAMING, recorded (design §18.3.4 called the normaliser `e164Of`). `packages/domain` already exports
 * an `e164Of(raw, countryCallingCode) => string | null` (`../template/survey/panel.ts`, the survey
 * panel and contact-list reader); a second export of that name from the package root is a compile
 * error, and silently shadowing one is worse. The SMS normaliser is therefore {@link smsDestinationOf}.
 * It is built on that same `e164Of`, so the two can never disagree about what a number is.
 */
import { callingCodeOf } from "../account/country-calling-code.js";
import { nationalNumberRuleOf } from "../telephony/sip-trunk/national-number-rule.js";
import { e164Of } from "../template/survey/panel.js";
/** ISO 3166-1 alpha-2 → the E.164 pattern of that country's mobile numbers. Greece: `+3069` + 8 digits. */
export const SMS_MOBILE_RANGE = Object.freeze({ GR: /^\+3069\d{8}$/ });
/**
 * E.164 normalisation of what the caller said: digits, an optional leading `+` (or `00`), a national
 * number read with `region` — the ISO country the caller called from — and **refuses rather than
 * guesses**: `null` when the number is not a number, when a national number has no region to be read
 * with, or when the result lies in no country of {@link SMS_MOBILE_RANGE}'s calling codes.
 *
 * The country is the one whose calling code the E.164 number starts with, among the countries SMS may
 * be sent to at all — so it is never a guess from a shared prefix outside that list.
 */
export function smsDestinationOf(input, region) {
    const regionCallingCode = region === null ? undefined : callingCodeOf(region);
    const e164 = e164Of(input, regionCallingCode);
    if (e164 === null)
        return null;
    const country = smsCountryOf(e164);
    return country === null ? null : Object.freeze({ e164, country });
}
/** The one country of {@link SMS_MOBILE_RANGE} whose calling code `e164` starts with, or `null`. */
function smsCountryOf(e164) {
    const matching = Object.keys(SMS_MOBILE_RANGE).filter((country) => {
        const code = callingCodeOf(country);
        return code !== undefined && e164.startsWith(`+${code}`);
    });
    const [country] = matching;
    return matching.length !== 1 || country === undefined ? null : country;
}
/** True only when `e164` is a mobile number of `country` by {@link SMS_MOBILE_RANGE}. */
export function isSmsMobile(e164, country) {
    const range = SMS_MOBILE_RANGE[country];
    return range !== undefined && range.test(e164);
}
export const SMS_MOBILE_NATIONAL_FORM = Object.freeze({
    GR: Object.freeze({ leadingDigit: "69", nationalLength: 10 }),
});
const UNREADABLE = Object.freeze({ kind: "unreadable" });
/** Digits and the separators people put between them when a number is written down as heard. */
const SAID_NUMBER = /^\+?[\d\s().\-/]+$/;
/**
 * Reads a number in the forms people say it — national (`6957205552`), spaced (`6957 2055 52`), with
 * the calling code (`+30 695…`, `0030 695…`, or `30 695…` with neither `+` nor `00`) — into E.164,
 * reading a national number in `region` (the conversation's country), and says whether it is a mobile.
 *
 * Where the region has a reviewed national number rule (`NATIONAL_NUMBER_RULE`), the LENGTH decides
 * between "national" and "calling code without the plus" — never a guess from shared leading digits —
 * and a number of any other length is `unreadable`, as is an international one whose national part
 * has the wrong length. A region without a rule reads as the survey reader does ({@link e164Of}).
 * Anything but digits and separators (words, letters) is `unreadable`: nothing is picked out of prose.
 */
export function smsMobileOf(said, region) {
    const trimmed = said.trim();
    if (!SAID_NUMBER.test(trimmed))
        return UNREADABLE;
    const digit = trimmed.replace(/\D/g, "");
    if (digit === "")
        return UNREADABLE;
    const international = trimmed.startsWith("+")
        ? digit
        : digit.startsWith("00")
            ? digit.slice(2)
            : internationalDigitOf(digit, region);
    if (international === null)
        return UNREADABLE;
    const e164 = e164Of(`+${international}`, undefined);
    if (e164 === null)
        return UNREADABLE;
    const country = smsCountryOf(e164);
    if (country === null)
        return Object.freeze({ kind: "foreign", e164 });
    const rule = nationalNumberRuleOf(country);
    if (rule !== undefined && e164.length - 1 - rule.callingCode.length !== rule.nationalSignificantLength)
        return UNREADABLE;
    return Object.freeze({ kind: isSmsMobile(e164, country) ? "mobile" : "not_mobile", e164, country });
}
/** A national number's international digits (calling code first) in `region`, or `null`. */
function internationalDigitOf(digit, region) {
    if (region === null)
        return null;
    const country = region.trim().toUpperCase();
    const rule = nationalNumberRuleOf(country);
    if (rule === undefined) {
        const read = e164Of(digit, callingCodeOf(country));
        return read === null ? null : read.slice(1);
    }
    const { callingCode, nationalSignificantLength, trunkPrefix } = rule;
    // The calling code said without its `+` or `00` ("30 6957 2055 52").
    if (digit.length === callingCode.length + nationalSignificantLength && digit.startsWith(callingCode))
        return digit;
    if (digit.length === trunkPrefix.length + nationalSignificantLength && digit.startsWith(trunkPrefix)) {
        return `${callingCode}${digit.slice(trunkPrefix.length)}`;
    }
    return null;
}
//# sourceMappingURL=sms-destination.js.map